import Link from 'next/link';
import React, { FC, useState } from 'react';

import Fireworks from '@fireworks-js/react';
import { Box, Button, Stack, Text } from '@mantine/core';
import { ContextModalProps, closeAllModals } from '@mantine/modals';

import { deepClone } from 'helpers/deep-clone';

import { FormOnSubmit } from 'components/forms/Form';
import { ModalTitle } from 'components/modals/ModalTitle';
import {
    ProjectInfoForm,
    ProjectInfoFormValuesStrict,
    ProjectInfoValidatorStrict,
} from 'components/project/components/ProjectInfoForm';
import { useCurrentProject } from 'hooks/use-current-project';
import { useCurrentTeamCompanies } from 'hooks/use-current-team-companies';
import { ProjectService } from 'services/ProjectService';

const PublishModal: FC<ContextModalProps> = () => {
    const [published, setPublished] = useState(false);

    const project = useCurrentProject();
    const { companies: currentProfiles } = useCurrentTeamCompanies();

    const handleClose = () => {
        closeAllModals();
    };

    if (!project) return null;

    const publish: FormOnSubmit<ProjectInfoFormValuesStrict> = async (values) => {
        const payload = ProjectInfoValidatorStrict.safeParse({
            ...values,
            visibility: 'marketplace',
        });

        if (payload.success) {
            await ProjectService.update(project.id, payload.data);

            setPublished(true);
        }
    };

    const defaultValues = deepClone(project) as ProjectInfoFormValuesStrict;

    const prefillProfile = !project.template?.profile && currentProfiles?.length === 1;

    if (prefillProfile) {
        defaultValues.template.profile = currentProfiles[0].id;
    }

    if (published) {
        return (
            <Stack>
                <ModalTitle>
                    Congrats,
                    <br /> your design is published!
                </ModalTitle>
                <Box h={300} style={{ overflow: 'hidden' }}>
                    <Fireworks />
                </Box>

                <Stack gap={4}>
                    <Button fullWidth component={Link} href="/designs" onClick={handleClose}>
                        View all reference designs
                    </Button>
                    <Button fullWidth variant="subtle" onClick={handleClose} bg="transparent">
                        Close
                    </Button>
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack>
            <ModalTitle mt="lg">
                Publish your design{' '}
                <Text ta="center" c="dimmed">
                    as a Reference Design
                </Text>
            </ModalTitle>

            <ProjectInfoForm
                showPublishFields
                useStrictValidation
                publish={publish}
                defaultValues={defaultValues}
                saveProps={{ label: 'Publish This Design', fullWidth: true, size: 'lg' }}
            />
        </Stack>
    );
};

export { PublishModal };
